import { Card } from '@/types/Card';
import {
  CARD_KEYS,
  CHANNEL_KEYS,
  CHANNEL_NAMES,
  BANK_KEYS,
  BANK_NAMES,
} from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks';

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.TAISHIN]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/',
    lastUpdated: '2025-05-04',
  },
};

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  // Taiwan Shin Bank cards
  [CARD_KEYS.TAISHIN_GOGORO]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg042/card001/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=273',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_GOGO]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg021/card001/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=192',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_BUSINESS]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg005/card001/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=207',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_TZUKUANG]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg023/card002/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_JKOS]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg038/card001/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=260',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_FLYGO]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg018/flygo/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=257',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_SUNROSE]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg046/card001/',
    applyUrl:
      'https://www.taishinbank.com.tw/eService/CreditCardAP/apply/m.jsp?c=0B7647D603E04E33C2E2553691E96D3F6CA0CDCF24AD88A77AA2D3DC4E5828BBC841DD&appid=TSCMS',
    lastUpdated: '2025-06-30',
  },
  [CARD_KEYS.TAISHIN_GIVING]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg040/card001/',
    applyUrl:
      'https://www.taishinbank.com.tw/eService/CreditCardAP/apply/m.jsp?c=0B7647D603E04E33C2E2553691E96D3F6CA0CDCF24AD88A77AA2D3DC4E5828BBC841DD&appid=TSCMS',
    lastUpdated: '2025-06-30',
  },
};

export const taishinCards: { [key: string]: Card } = {
  [CARD_KEYS.TAISHIN_GOGO]: {
    name: '台新@GoGo卡',
    summary: '指定網購、行動支付最高3%回饋，新戶最高30%',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'VISA/Mastercard',
    picture: 'logo_taishin-gogo.png',
    key: 'taishin-gogo',
    link: cardLinkMap[CARD_KEYS.TAISHIN_GOGO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_GOGO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '每月最高回饋300點',
        simplified_condition: '指定網購平台',
        condition:
          '於指定網購平台(蝦皮shopee、momo、酷澎coupang等)消費，享3%台新Point回饋，適用期間2024/12/1~2025/8/31結帳帳單',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '每月最高回饋300點',
        simplified_condition: '指定行動支付',
        condition:
          '使用指定行動支付(街口支付、Apple Pay等)消費，享3%台新Point回饋，適用期間2024/12/1~2025/8/31結帳帳單',
        channel: '行動支付',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '每月最高回饋300點',
        simplified_condition: '指定訂房網站',
        condition:
          '於指定國際訂房網站(Agoda、Trip.com等)消費，享3%台新Point回饋，適用期間2024/12/1~2025/8/31結帳帳單',
        channel: '訂房',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '最高10% 回饋',
        max_feedback: '優惠券不限量',
        simplified_condition: '@GoGo卡感謝祭',
        condition:
          '領券立享指定通路(百貨/全聯/超商/外送/餐飲/網購/中油/旅行社/訂房網/航空/日本)最高10%回饋，優惠券不限量',
        channel: '指定通路',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '最高30% 回饋',
        max_feedback: '新戶限定',
        simplified_condition: '新戶綁Pay加碼',
        condition:
          '新戶綁定Apple Pay/街口支付等行動支付，享最高20%加碼回饋，加計指定通路優惠券最高可達30%回饋',
        channel: '行動支付',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.3% 回饋',
        max_feedback: '須登錄',
        simplified_condition: '保費一次付清',
        condition: '使用@GoGo卡繳交保費一次付清，享最高1.3%回饋(須登錄)',
        channel: '保險',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: 'NT$2,000萬',
        max_feedback: '國內外皆適用',
        simplified_condition: '旅遊平安險',
        condition: '持卡享有最高NT$2,000萬旅遊平安險保障，國內外旅遊皆適用',
        channel: '旅遊',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '最高14%',
        max_feedback: 'Agoda最高現折8%',
        simplified_condition: '訂房優惠',
        condition:
          '於Agoda專屬網頁刷@GoGo卡訂房享最高現折8%，Booking.com最高現折10%，Trip.com最高現折6%',
        channel: '訂房',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.TAISHIN_BUSINESS]: {
    name: '台新商務御璽卡',
    summary: '商務人士必備，海外最高3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'VISA',
    picture: 'logo_taishin-business.png',
    key: 'taishin-business',
    link: cardLinkMap[CARD_KEYS.TAISHIN_BUSINESS].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_BUSINESS].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '使用台新商務御璽卡於海外消費，享3%台新Point回饋無上限',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '國內航空、訂房',
        condition: '使用台新商務御璽卡於國內航空公司、訂房網站消費，享2%台新Point回饋無上限',
        channel: '旅遊',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場接送',
        max_feedback: '每年2次',
        simplified_condition: '當月消費滿額',
        condition: '當月消費滿NT$80,000，可享免費機場接送服務，每年最多2次',
        channel: '機場接送',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '3%',
    specific_channel_feedback: '2%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.TAISHIN_TZUKUANG]: {
    name: '台新燦坤生活卡',
    summary: '燦坤消費最高10%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'Mastercard',
    picture: 'logo_taishin-tzukuang.png',
    key: 'taishin-tzukuang',
    link: cardLinkMap[CARD_KEYS.TAISHIN_TZUKUANG].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_TZUKUANG].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 台新Point',
        max_feedback: '每月最高回饋500點',
        simplified_condition: '燦坤消費',
        condition: '於燦坤3C實體門市或官網消費，享10%台新Point回饋，每月最高回饋500點',
        channel: '3C家電',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 台新Point',
        max_feedback: '每月最高回饋300點',
        simplified_condition: '指定超市、量販店',
        condition:
          '於指定超市、量販店(家樂福、全聯、頂好、愛買)消費，享5%台新Point回饋，每月最高回饋300點',
        channel: '超市',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 台新Point',
        max_feedback: '每月最高回饋200點',
        simplified_condition: '指定加油站',
        condition: '於指定加油站(中油、台塑)消費，享2%台新Point回饋，每月最高回饋200點',
        channel: '加油',
      },
    ],
    domestic_feedback: '0.5%',
    international_feedback: '1.5%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.TAISHIN_GOGORO]: {
    name: '台新Gogoro Rewards聯名卡',
    summary: 'Gogoro資費最高10%回饋，海外最高4%',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'VISA',
    picture: 'logo_taishin-gogoro.png',
    key: 'taishin-gogoro',
    link: cardLinkMap[CARD_KEYS.TAISHIN_GOGORO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_GOGORO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% Gogoro Points',
        max_feedback: '回饋無上限',
        simplified_condition: '電池資費帳單',
        condition:
          '使用台新Gogoro Rewards聯名卡繳納Gogoro電池資費帳單，最高享10% Gogoro Smart Points回饋無上限',
        channel: '電池資費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '15% Gogoro Points',
        max_feedback: '新戶首刷禮',
        simplified_condition: 'GoShare消費',
        condition: '使用台新Gogoro Rewards聯名卡於GoShare消費，最高享15% Gogoro Smart Points回饋',
        channel: '共享機車',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '4% Gogoro Points',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '使用台新Gogoro Rewards聯名卡於海外消費，最高享4% Gogoro Smart Points回饋無上限',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% Gogoro Points',
        max_feedback: '回饋無上限',
        simplified_condition: '指定通路消費',
        condition:
          '使用台新Gogoro Rewards聯名卡於指定超商/高鐵/外送平台/旅遊平台消費，最高享3% Gogoro Smart Points回饋',
        channel: '指定通路',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% Gogoro Points',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '使用台新Gogoro Rewards聯名卡一般消費，最高享1% Gogoro Smart Points回饋無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '4%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.TAISHIN_JKOS]: {
    name: '台新街口聯名卡',
    summary: '一般消費1%街口幣無上限',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'VISA',
    picture: 'logo_taishin-jkos.gif',
    key: 'taishin-jkos',
    link: cardLinkMap[CARD_KEYS.TAISHIN_JKOS].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_JKOS].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 街口幣',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '使用街口支付APP綁定台新街口聯名卡，一般消費(含街口繳費)享1%街口幣無上限',
        channel: '一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '7.5% 街口幣',
        max_feedback: '回饋無上限',
        simplified_condition: '日本PayPay交易',
        condition:
          '使用街口支付APP綁定台新街口聯名卡，日本PayPay交易享1%街口幣無上限，再享街口活動加碼最高5%街口幣，且免1.5%海外交易手續費',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 街口幣',
        max_feedback: '回饋無上限',
        simplified_condition: '指定通路消費',
        condition:
          '於街口支付App綁定台新街口聯名卡，於量販、KLOOK、91APP等百大合作通路消費，享3%街口幣無上限',
        channel: '指定通路',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '14% 街口幣',
        max_feedback: '鈦金商務卡新戶獨享',
        simplified_condition: '日本PayPay交易',
        condition: '街口支付App綁定新申辦街口Mastercard鈦金商務卡，日本PayPay交易享最高14%',
        channel: '海外消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '14%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.TAISHIN_FLYGO]: {
    name: '台新FlyGo鈦金商務卡',
    summary: '航旅最高5%、海外最高3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'Mastercard',
    picture: 'logo_taishin-flygo.png',
    key: 'taishin-flygo',
    link: cardLinkMap[CARD_KEYS.TAISHIN_FLYGO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_FLYGO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '精選航旅',
        condition:
          '精選航旅消費享最高5%台新Point回饋，包含華航、長榮、星宇、虎航、國泰航空、華信、立榮、Klook、東南旅遊',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '海外消費(含網購)享最高3%台新Point回饋，無上限',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.3% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享0.3%台新Point回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '0.3%',
    international_feedback: '3%',
    specific_channel_feedback: '5%',
    start_date: '2024-12-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.TAISHIN_GIVING]: {
    name: '台新玫瑰Giving卡',
    summary: '海外3%、節假日2%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'Mastercard/VISA',
    picture: 'logo_taishin-giving.jpg',
    key: 'taishin-giving',
    link: cardLinkMap[CARD_KEYS.TAISHIN_GIVING].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_GIVING].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '節假日消費',
        condition:
          '節假日消費享最高2%台新Point回饋，需申辦「台新信用卡數位帳單」及設定「台新帳戶扣繳台新信用卡費」',
        channel: '國內節假日',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition:
          '海外消費享最高3%台新Point回饋，需申辦「台新信用卡數位帳單」及設定「台新帳戶扣繳台新信用卡費」',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '平日消費',
        condition:
          '平日消費享1%台新Point回饋，需申辦「台新信用卡數位帳單」及設定「台新帳戶扣繳台新信用卡費」',
        channel: '國內平日',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '居家清潔服務',
        max_feedback: '每年上限２次',
        simplified_condition: '平日消費滿額',
        condition: '國內平日消費滿ＮＴ＄９萬享居家清潔服務一次，每年上限２次',
        channel: '居家清潔',
      },
    ],
    domestic_feedback: '2%',
    international_feedback: '3%',
    specific_channel_feedback: '3%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.TAISHIN_SUNROSE]: {
    name: '台新太陽/玫瑰卡(切換刷)',
    summary: '指定消費最高3.3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'Mastercard/JCB',
    picture: 'logo_taishin-sunrose.gif',
    key: 'taishin-sunrose',
    link: cardLinkMap[CARD_KEYS.TAISHIN_SUNROSE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_SUNROSE].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 台新Point',
        max_feedback: '每期帳單回饋上限10,000點',
        simplified_condition: '天天刷方案',
        condition:
          '天天刷方案適用超商、便利商店、加油站、指定百貨、指定訂房旅遊消費，最高3.3%台新Point回饋',
        channel: '日常消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 台新Point',
        max_feedback: '每期帳單回饋上限10,000點',
        simplified_condition: '大筆刷方案',
        condition:
          '大筆刷方案適用單筆10,000元以上消費，包含指定百貨、指定訂房旅遊消費，最高3.3%台新Point回饋',
        channel: '大額消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 台新Point',
        max_feedback: '每期帳單回饋上限10,000點',
        simplified_condition: '好饗刷方案',
        condition: '好饗刷方案適用餐飲店家、餐廳、外送平台消費，最高3.3%台新Point回饋',
        channel: '餐飲',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 台新Point',
        max_feedback: '每期帳單回饋上限10,000點',
        simplified_condition: '海外指定國家消費',
        condition: '在日本、韓國、歐洲、美國等指定國家消費，免切換即享最高3.3%台新Point回饋',
        channel: '海外消費',
      },
    ],
    domestic_feedback: '0.3%',
    international_feedback: '3.3%',
    specific_channel_feedback: '3.3%',
    start_date: '2025-01-02',
    end_date: '2025-06-30',
  },
};
